import { storageService } from '@/services/storage'

/**
 * Generic local JSON persistence for Settings Center sections.
 * Defaults come from bundled *.json; overrides live in localStorage.
 */
export class LocalJsonStore<T> {
  private readonly storageKey: string
  private readonly defaults: T

  constructor(storageKey: string, defaults: T) {
    this.storageKey = storageKey
    this.defaults = defaults
  }

  load(): T {
    if (!storageService.has(this.storageKey)) {
      return clone(this.defaults)
    }
    const stored = storageService.getJSON<T>(this.storageKey, this.defaults)
    return mergeDefaults(this.defaults, stored)
  }

  save(value: T): boolean {
    return storageService.setJSON(this.storageKey, value)
  }

  reset(): T {
    storageService.remove(this.storageKey)
    return clone(this.defaults)
  }

  getDefaults(): T {
    return clone(this.defaults)
  }
}

function clone<T>(value: T): T {
  return structuredClone(value)
}

function mergeDefaults<T>(defaults: T, stored: T): T {
  if (Array.isArray(defaults)) {
    return (Array.isArray(stored) ? stored : defaults) as T
  }
  if (defaults && typeof defaults === 'object') {
    const base = { ...(defaults as Record<string, unknown>) }
    const overlay = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {}
    for (const [key, value] of Object.entries(overlay)) {
      if (value !== undefined) base[key] = value
    }
    return base as T
  }
  return stored ?? defaults
}
